/* eslint-disable no-restricted-imports */
import React, {useMemo, useRef} from 'react'
import {UIGameActionButton} from '../../../common/UIGameActionButton'
import {
    UIGameResourceCardsTooltipContent,
    UIGameResourceCardsTooltipContentHandle,
} from '../../../common/UIGameResourceCardsTooltipContent'
import {getCardsForPiece} from '@colonist/game-card-types'
import {PieceTypes} from '@colonist/game-base-types'
import {useActionButtonCommonErrors} from '../utils'
import {ROAD_IMAGE_PATH} from './utils'
import {useGameStore} from '../../../store/GameStoreContext'
import {usePlayerValidators} from '../../../store/selectors/usePlayerValidators'
import {ActionState} from '@colonist/game-states'
import {TranslationObject} from '@colonist/i18n'
import {useUIGameSocketContext} from '../../../contexts/UIGameSocketContext'
import {useCurrentStateValidator} from '../../../store/selectors/useCurrentStateValidator'
import {HiddenUIElement} from '@colonist/game-beginner-mode'
import clsx from 'clsx'
import styles from '../UIGameActionButtonContainer.module.scss'
import {useMechanicValidatorRoad} from '../../../store/selectors/useMechanicValidatorRoad'

const cardsToBuildRoad = getCardsForPiece(PieceTypes.Road)

export const UIGameActionButtonRoad = ({
    className,
}: {
    className?: string
}) => {
    const myColor = useGameStore((state) => state.myColor)
    const currentStateValidator = useCurrentStateValidator()
    const {getPlayerValidator} = usePlayerValidators()
    const playerValidator = getPlayerValidator(myColor)
    const tooltipRef = useRef<UIGameResourceCardsTooltipContentHandle>(null)
    const {errorHelperText, hasCommonErrors} = useActionButtonCommonErrors()
    const {sendBuildRoad, sendCancelAction, sendClearRoadEdgeHighlight} = useUIGameSocketContext()
    const beginnerActionState = useGameStore((state) => state.gameBeginnerHint.beginnerActionState)
    const roadValidator = useMechanicValidatorRoad()

    const actionStatus = useMemo(() => {
        if(!roadValidator) {
            return {canTakeAction: false}
        }
        const useResources = true
        const canPlaceRoadActionStatus = roadValidator.canPlayerPlaceRoad(myColor, useResources)

        return canPlaceRoadActionStatus
    }, [roadValidator, myColor])

    const triggerFlickerCardsAnimation = () => {
        const notEnoughResources = !playerValidator.hasResourceCardsInHand(cardsToBuildRoad)
        if(notEnoughResources) {
            tooltipRef.current?.triggerFlickerAnimation()
        }
    }

    const isHidden = beginnerActionState?.hiddenUIElements.includes(HiddenUIElement.RoadActionButton)
    const isSomePlayerSelectingToDiscard = currentStateValidator.isSomePlayerInActionState(ActionState.SelectCardsToDiscard)
    const disabled = hasCommonErrors || !actionStatus.canTakeAction || isHidden || isSomePlayerSelectingToDiscard
    const helperText: TranslationObject | undefined = errorHelperText || actionStatus.errorMessageIfCantTakeAction
    const icon = ROAD_IMAGE_PATH[myColor]
    const actionState = currentStateValidator.getPlayerActionState(myColor)
    const isPlacingRoad = actionState === ActionState.PlaceRoad && !disabled
    const bankRoadAmount = roadValidator?.getRoadPlayerState(myColor).bankRoadAmount ?? 0

    const onClick = () => {
        if(disabled) return
        if(isPlacingRoad) {
            sendClearRoadEdgeHighlight()
            sendCancelAction()
        } else {
            sendBuildRoad()
        }
    }

    return <UIGameActionButton
        className={clsx(className, {
            [styles.hidden]: isHidden,
        })}
        count={bankRoadAmount}
        countColor={myColor}
        disabled={disabled}
        helperText={helperText}
        highlight={isPlacingRoad}
        iconPath={icon}
        onClickActionIfDisabled={triggerFlickerCardsAnimation}
        tooltipCustomMenuComponent={
            <UIGameResourceCardsTooltipContent
                cardInventory={playerValidator.resourceCards.cards}
                cardsToBuild={cardsToBuildRoad}
                ref={tooltipRef}/>
        }
        onClick={onClick}
    />
}
